import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { notesApi } from '@/lib/notesApi';
import { fmtISO, prettyDateLong, normalizeTime, addMinutesToTime } from './plannerUtils';
import {
  CalendarClock, FileText, CalendarPlus, Users, Target,
  ArrowRight, Loader2, CheckCircle2, Circle, CalendarRange,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/* Smart date parsing — natural language + DD-MM                               */
/* -------------------------------------------------------------------------- */
const WEEKDAYS = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

// Resolve a free-text date expression to an ISO 'YYYY-MM-DD' (local), or null.
// Supports:
//   • "today", "tomorrow", "yesterday" (+ short forms)
//   • "in N days"
//   • a weekday, optionally prefixed with "this" / "coming" / "next"
//       - bare / this / coming → the nearest upcoming occurrence (today counts)
//       - next → forced to a future occurrence (if today is that weekday, +7)
//   • "DD-MM" / "DD/MM" / "DD.MM" (optional "-YYYY"). Year rule: current year
//     unless the typed month is EARLIER than the current month, then next year.
export function parseDateQuery(raw) {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };

  if (s === 'today' || s === 'tdy') return fmtISO(today);
  if (['tomorrow', 'tmrw', 'tmr', 'tom', 'tomo'].includes(s)) return fmtISO(addDays(today, 1));
  if (['yesterday', 'yest', 'ytd'].includes(s)) return fmtISO(addDays(today, -1));

  let m = s.match(/^in\s+(\d{1,3})\s*(?:d|days?)$/);
  if (m) return fmtISO(addDays(today, parseInt(m[1], 10)));

  // weekday with optional prefix
  m = s.match(/^(next|this|coming|nxt)?\s*([a-z]+)$/);
  if (m && Object.prototype.hasOwnProperty.call(WEEKDAYS, m[2])) {
    const target = WEEKDAYS[m[2]];
    let delta = (target - today.getDay() + 7) % 7; // 0..6 (0 = today)
    if ((m[1] === 'next' || m[1] === 'nxt') && delta === 0) delta = 7;
    return fmtISO(addDays(today, delta));
  }

  // DD-MM[-YYYY]
  m = s.match(/^(\d{1,2})\s*[-/.]\s*(\d{1,2})(?:\s*[-/.]\s*(\d{2,4}))?$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const curMonth = today.getMonth() + 1;
    const curYear = today.getFullYear();
    let year;
    if (m[3]) {
      year = parseInt(m[3], 10);
      if (year < 100) year += 2000;
    } else {
      year = month < curMonth ? curYear + 1 : curYear;
    }
    const dt = new Date(year, month - 1, day);
    if (dt.getMonth() !== month - 1 || dt.getDate() !== day) return null; // reject e.g. 31-02
    return fmtISO(dt);
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Quick-create NLP — "create event/meeting <title> <date> at <time> invite …" */
/* -------------------------------------------------------------------------- */

// Match a leading create intent. Captures the entity word + the remainder.
//   e.g. "create event Lunch tomorrow at 1pm" → kind "event", rest "Lunch tomorrow at 1pm"
const INTENT_RE = /^\s*(?:create|new|add|schedule|make|set\s*up)?\s*(events?|meetings?|mtgs?|appointments?|goals?)\b\s*(.*)$/i;

// Pretty 12-hour label for an 'HH:MM' time (e.g. "13:00" → "1:00 PM").
export function pretty12(t) {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return null;
  const ap = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m || 0).padStart(2, '0')} ${ap}`;
}

// Split an invitee blob into individual names: "Rahul, Asha and Mike" → [...].
function splitNames(str) {
  return String(str || '')
    .split(/\s*(?:,|&|\/|\+|\band\b|\bwith\b)\s*/i)
    .map((x) => x.trim())
    .filter(Boolean);
}

// Pull an explicit time phrase out of free text. Accepts "at 1pm", "at 15:30",
// "9am", "0930", "16:00". Returns { time: 'HH:MM'|null, rest } with the matched
// phrase removed. Bare day/month numbers (e.g. "26-01") are never matched here.
function extractTime(text) {
  const re = /\bat\s+(\d{1,2}(?::\d{2})?\s*(?:[ap]\.?\s*m\.?)?)|\b(\d{1,2}:\d{2}\s*(?:[ap]\.?\s*m\.?)?)\b|\b(\d{1,2}\s*[ap]\.?\s*m\.?)\b/i;
  const m = text.match(re);
  if (!m) return { time: null, rest: text };
  const token = (m[1] || m[2] || m[3] || '').trim();
  const norm = normalizeTime(token);
  if (!norm) return { time: null, rest: text };
  const rest = (text.slice(0, m.index) + ' ' + text.slice(m.index + m[0].length)).replace(/\s+/g, ' ').trim();
  return { time: norm, rest };
}

// Pull a trailing date phrase (today/tomorrow/next monday/DD-MM/"in 3 days") off
// the end of the text, preferring the LONGEST match so "next monday" beats
// "monday". A leading "on" is tolerated ("… on friday"). Returns { dateIso, rest }.
function extractTrailingDate(text) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  for (let k = Math.min(3, words.length); k >= 1; k--) {
    const phrase = words.slice(words.length - k).join(' ');
    const cand = phrase.replace(/^on\s+/i, '');
    const iso = parseDateQuery(cand);
    if (iso) {
      const rest = words.slice(0, words.length - k).join(' ').replace(/\s+on$/i, '').trim();
      return { dateIso: iso, rest };
    }
  }
  return { dateIso: null, rest: text };
}

// Full parse of a quick-create command. Returns null when no create intent is
// present (so the palette falls back to search / generic create).
export function parseQuickCreate(raw) {
  const s = (raw || '').trim();
  if (!s) return null;
  const m = s.match(INTENT_RE);
  if (!m) return null;
  const word = m[1].toLowerCase();
  let kind;
  if (word.startsWith('event') || word.startsWith('appointment')) kind = 'event';
  else if (word.startsWith('meeting') || word.startsWith('mtg')) kind = 'meeting';
  else kind = 'goal';

  let rest = (m[2] || '').trim();

  // Invitees (meetings only) — everything after the "invite" keyword.
  let inviteNames = [];
  if (kind === 'meeting') {
    const im = rest.match(/\binvit(?:e|es|ed|ing)?\b/i);
    if (im) {
      inviteNames = splitNames(rest.slice(im.index + im[0].length));
      rest = rest.slice(0, im.index).trim();
    }
  }

  const { time, rest: afterTime } = extractTime(rest);
  const { dateIso, rest: afterDate } = extractTrailingDate(afterTime);
  const title = afterDate.replace(/\s+/g, ' ').trim();
  return { kind, title, dateIso, time, inviteNames };
}

// Resolve typed invitee names to directory user ids (case-insensitive, by name
// or email prefix). Unmatched names are ignored — the dialog's picker lets the
// user add anyone manually.
function resolveInvitees(names, directory) {
  const ids = [];
  const used = new Set();
  for (const nm of (names || [])) {
    const low = nm.toLowerCase();
    const found = (directory || []).find((u) => !used.has(u.id)
      && (((u.name || '').toLowerCase().includes(low)) || ((u.email || '').toLowerCase().startsWith(low))));
    if (found) { ids.push(found.id); used.add(found.id); }
  }
  return ids;
}

const priorityTone = {
  high: 'text-rose-500',
  medium: 'text-amber-500',
  low: 'text-sky-500',
};

/* -------------------------------------------------------------------------- */
/* Command palette                                                             */
/* -------------------------------------------------------------------------- */
export default function PlannerCommandPalette({
  open,
  onOpenChange,
  onPickDate,
  onOpenNote,
  onOpenTask,
  onOpenItem,
  onEditGoal,
  onCreateEvent,
  onCreateMeeting,
  onCreateGoal,
  directory = [],
}) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState({ tasks: [], events: [], meetings: [], goals: [], notes: [] });
  const debRef = useRef(null);
  const reqRef = useRef(0);

  // Reset query each time the palette opens.
  useEffect(() => {
    if (open) {
      setQ('');
      setRes({ tasks: [], events: [], meetings: [], goals: [], notes: [] });
      setLoading(false);
    }
  }, [open]);

  // Debounced unified search (planner entities + notes). Runs even alongside a
  // quick-create command so a legitimate title search (e.g. a note called
  // "meeting notes") is never hidden — the Quick-create group renders above it.
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    const term = q.trim();
    if (!term) {
      setRes({ tasks: [], events: [], meetings: [], goals: [], notes: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debRef.current = setTimeout(async () => {
      const rid = ++reqRef.current;
      try {
        const [pl, nt] = await Promise.all([
          notesApi.get('/planner/command-search', { params: { q: term, limit: 6 } }).then((r) => r.data).catch(() => ({})),
          notesApi.get('/notes/notes/search', { params: { q: term, limit: 6 } }).then((r) => r.data).catch(() => []),
        ]);
        if (rid !== reqRef.current) return; // stale response guard
        setRes({
          tasks: pl.tasks || [],
          events: pl.events || [],
          meetings: pl.meetings || [],
          goals: pl.goals || [],
          notes: Array.isArray(nt) ? nt : [],
        });
      } finally {
        if (rid === reqRef.current) setLoading(false);
      }
    }, 220);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [q]);

  // Close the palette, then run the action (so the next dialog/nav isn't
  // fighting the palette's own close/focus-trap teardown).
  const run = useCallback((fn) => {
    onOpenChange(false);
    setTimeout(() => { try { fn(); } catch { /* noop */ } }, 20);
  }, [onOpenChange]);

  const dateIso = useMemo(() => parseDateQuery(q), [q]);
  const quick = useMemo(() => parseQuickCreate(q), [q]);
  const term = q.trim();
  // Prefill new items with the typed text — but not when it's a date expression
  // or a structured quick-create command (which carries its own parsed title).
  const createTitle = term && !dateIso && !quick ? term : '';
  const hasResults = res.tasks.length || res.events.length || res.meetings.length || res.goals.length || res.notes.length;

  // Build a prefill payload for a create action. When a quick-create command is
  // active it carries the parsed title/date/time (and resolved invitees for
  // meetings); otherwise it falls back to the raw typed title.
  const prefillFor = useCallback((kind) => {
    if (!quick) return { title: createTitle };
    const base = { title: quick.title };
    if (quick.dateIso) base.date = quick.dateIso;
    if (quick.time) { base.start_time = quick.time; base.end_time = addMinutesToTime(quick.time, 30); }
    if (kind === 'meeting') base.attendeeIds = resolveInvitees(quick.inviteNames, directory);
    return base;
  }, [quick, createTitle, directory]);

  // Preview chips for a quick-create row.
  const previewFor = useCallback((kind) => {
    if (!quick || kind === 'goal') return [];
    const bits = [];
    if (quick.dateIso) bits.push(prettyDateLong(quick.dateIso));
    bits.push(quick.time ? pretty12(quick.time) : 'set time');
    if (kind === 'meeting') {
      const n = resolveInvitees(quick.inviteNames, directory).length;
      if (n) bits.push(`${n} invited`);
    }
    return bits;
  }, [quick, directory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="notes-scope overflow-hidden border-[var(--n-border)] bg-[var(--n-surface)] p-0 shadow-2xl sm:max-w-[640px]" data-testid="planner-command-palette">
        <DialogTitle className="sr-only">Planner command palette</DialogTitle>
        <Command
          shouldFilter={false}
          className="bg-transparent [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[color:var(--p-muted)]"
        >
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Search, type a date, or “meeting Sync next monday at 10am invite Asha”"
            data-testid="planner-command-input"
            className="text-[color:var(--p-text)]"
          />
          <CommandList className="max-h-[420px] px-1 py-1.5">
            {loading && (
              <div className="flex items-center gap-2 px-3 py-2 text-[12.5px] text-[color:var(--p-muted)]" data-testid="planner-command-loading">
                <Loader2 size={13} className="animate-spin" /> Searching…
              </div>
            )}

            {!term && !loading && (
              <div className="px-3 py-2 text-[12.5px] text-[color:var(--p-muted)]">
                Type to search, jump to a date like <span className="font-semibold text-[color:var(--p-text-soft)]">26-11</span>, or create with
                <span className="font-semibold text-[color:var(--p-text-soft)]"> “event Lunch with Sam tomorrow at 1pm”</span>.
              </div>
            )}

            {term && !loading && !hasResults && !dateIso && !quick && (
              <CommandEmpty className="px-3 py-6 text-center text-[13px] text-[color:var(--p-muted)]">No matches for “{term}”.</CommandEmpty>
            )}

            {/* Jump to date */}
            {dateIso && (
              <CommandGroup heading="Jump to date">
                <CommandItem
                  value={`__date-${dateIso}`}
                  onSelect={() => run(() => onPickDate(dateIso))}
                  data-testid="planner-command-jump-date"
                  className="cursor-pointer gap-2.5 rounded-lg px-3 py-2.5 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]"
                >
                  <CalendarClock size={16} className="text-[var(--n-accent)]" />
                  <span>Go to <span className="font-semibold">{prettyDateLong(dateIso)}</span></span>
                  <ArrowRight size={14} className="ml-auto text-[color:var(--p-muted-2)]" />
                </CommandItem>
              </CommandGroup>
            )}

            {/* Quick create — structured "create event/meeting …" command with
                parsed title, date, time and (for meetings) invitees. Shown first
                so Enter triggers the create action for a command-style query. */}
            {quick && (
              <CommandGroup heading="Quick create">
                {[quick.kind, ...['event', 'meeting', 'goal'].filter((k) => k !== quick.kind)].map((kind) => {
                  const Icon = kind === 'event' ? CalendarPlus : kind === 'meeting' ? Users : Target;
                  const label = kind === 'event' ? 'New event' : kind === 'meeting' ? 'New meeting' : 'New goal';
                  const onSel = kind === 'event'
                    ? () => onCreateEvent(prefillFor('event'))
                    : kind === 'meeting'
                      ? () => onCreateMeeting(prefillFor('meeting'))
                      : () => onCreateGoal(prefillFor('goal'));
                  const bits = previewFor(kind);
                  return (
                    <CommandItem key={`qc-${kind}`} value={`__qc-${kind}`} onSelect={() => run(onSel)}
                      data-testid={`planner-command-quick-${kind}`}
                      className="cursor-pointer gap-2.5 rounded-lg px-3 py-2.5 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                      <Icon size={16} className="text-[var(--n-accent)]" />
                      <span className="min-w-0 truncate">{label}{quick.title ? <> &ldquo;<span className="font-semibold">{quick.title}</span>&rdquo;</> : ''}</span>
                      {bits.length > 0 && (
                        <span className="ml-auto shrink-0 text-[11px] text-[color:var(--p-muted)]">{bits.join('  ·  ')}</span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* Tasks */}
            {res.tasks.length > 0 && (
              <CommandGroup heading="Tasks">
                {res.tasks.map((t) => (
                  <CommandItem key={t.id} value={`task-${t.id}`} onSelect={() => run(() => onOpenTask(t.id))}
                    data-testid={`planner-command-task-${t.id}`}
                    className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                    {t.completed
                      ? <CheckCircle2 size={15} className="text-emerald-500" />
                      : <Circle size={15} className={priorityTone[t.priority] || 'text-[color:var(--p-muted-2)]'} />}
                    <span className={`truncate ${t.completed ? 'line-through opacity-60' : ''}`}>{t.title}</span>
                    {t.due_at && <span className="ml-auto shrink-0 text-[11px] text-[color:var(--p-muted)]">{prettyDateLong(String(t.due_at).slice(0, 10))}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Events */}
            {res.events.length > 0 && (
              <CommandGroup heading="Events">
                {res.events.map((e) => (
                  <CommandItem key={e.id} value={`event-${e.id}`} onSelect={() => run(() => onOpenItem(e))}
                    data-testid={`planner-command-event-${e.id}`}
                    className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                    <CalendarRange size={15} className="text-[var(--n-accent)]" />
                    <span className="truncate">{e.title || 'Untitled event'}</span>
                    {(e.anchor_date || e.date) && <span className="ml-auto shrink-0 text-[11px] text-[color:var(--p-muted)]">{prettyDateLong(e.anchor_date || e.date)}{e.start_time ? ` · ${e.start_time}` : ''}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Meetings */}
            {res.meetings.length > 0 && (
              <CommandGroup heading="Meetings">
                {res.meetings.map((m) => (
                  <CommandItem key={m.id} value={`meeting-${m.id}`} onSelect={() => run(() => onOpenItem(m))}
                    data-testid={`planner-command-meeting-${m.id}`}
                    className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                    <Users size={15} className="text-[var(--n-accent)]" />
                    <span className="truncate">{m.title || 'Untitled meeting'}</span>
                    {(m.anchor_date || m.date) && <span className="ml-auto shrink-0 text-[11px] text-[color:var(--p-muted)]">{prettyDateLong(m.anchor_date || m.date)}{m.start_time ? ` · ${m.start_time}` : ''}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Goals */}
            {res.goals.length > 0 && (
              <CommandGroup heading="Goals">
                {res.goals.map((g) => (
                  <CommandItem key={g.id} value={`goal-${g.id}`} onSelect={() => run(() => onEditGoal(g))}
                    data-testid={`planner-command-goal-${g.id}`}
                    className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                    <Target size={15} className="text-[var(--n-accent)]" />
                    <span className="truncate">{g.title || 'Untitled goal'}</span>
                    {g.type && <span className="ml-auto shrink-0 text-[10.5px] uppercase tracking-wide text-[color:var(--p-muted-2)]">{g.type}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Notes */}
            {res.notes.length > 0 && (
              <CommandGroup heading="Notes">
                {res.notes.map((n) => (
                  <CommandItem key={n.id} value={`note-${n.id}`} onSelect={() => run(() => onOpenNote(n.id))}
                    data-testid={`planner-command-note-${n.id}`}
                    className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                    <FileText size={15} className="text-[var(--n-accent)]" />
                    <span className="truncate">{n.title || 'Untitled'}</span>
                    {n.folder_name && <span className="ml-auto shrink-0 text-[11px] text-[color:var(--p-muted)]">{n.folder_name}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Create actions (fallback). When the query isn't a date or a
                quick-create command, prefills the new item's title. */}
            {!quick && (
              <CommandGroup heading="Create">
                <CommandItem value="__create-event" onSelect={() => run(() => onCreateEvent(prefillFor('event')))}
                  data-testid="planner-command-create-event"
                  className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                  <CalendarPlus size={15} className="text-[var(--n-accent)]" /> New event{createTitle ? <span className="truncate text-[color:var(--p-muted)]">“{createTitle}”</span> : ''}
                </CommandItem>
                <CommandItem value="__create-meeting" onSelect={() => run(() => onCreateMeeting(prefillFor('meeting')))}
                  data-testid="planner-command-create-meeting"
                  className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                  <Users size={15} className="text-[var(--n-accent)]" /> New meeting{createTitle ? <span className="truncate text-[color:var(--p-muted)]">“{createTitle}”</span> : ''}
                </CommandItem>
                <CommandItem value="__create-goal" onSelect={() => run(() => onCreateGoal(prefillFor('goal')))}
                  data-testid="planner-command-create-goal"
                  className="cursor-pointer gap-2.5 rounded-lg px-3 py-2 text-[13px] text-[color:var(--p-text)] aria-selected:bg-[var(--n-accent-bg)]">
                  <Target size={15} className="text-[var(--n-accent)]" /> New goal{createTitle ? <span className="truncate text-[color:var(--p-muted)]">“{createTitle}”</span> : ''}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
